<script setup lang="ts">
import type { TemplateModule } from '@/types/types'
import type { Icons } from '@/components/base/BaseIcon.vue'

import { Loader2 } from 'lucide-vue-next'

const props = defineProps<{
  module?: TemplateModule
  hasDescription?: boolean
  title: string
  description?: string
  link?: string
  primaryButton?: {
    icon?: Icons
    isLoading?: boolean
    isOutlined?: boolean
    text: string
    link?: string
  }
  secondaryButton?: {
    icon?: Icons
    isOutlined?: boolean
    isLoading?: boolean
    text: string
    link?: string
  }
  icon?: Icons
}>()

const $router = useRouter()
const { t } = useI18n()
const imgUrl = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/empty-state/${props.module}.png`)

const redirect = () => {
  if (props.link !== 'custom') {
    $router.push({ name: props.link })
  } else {
    emit('redirect')
  }
}

const emit = defineEmits<{
  (e: 'onClick', id: string): void
  (e: 'redirect'): void
}>()
</script>

<template>
  <div class="mt-10 flex flex-col items-center gap-7 text-center">
    <img v-if="module" class="hidden sm:flex sm:h-52" :src="imgUrl" />
    <div class="my-7 space-y-2.5">
      <BaseIcon v-if="icon" :name="icon" class="mx-auto aspect-square h-6 text-muted-foreground" />
      <h2 class="text-xl font-bold text-foreground lg:text-2xl">
        {{ title }}
      </h2>
      <p v-if="description" class="mx-auto mt-4 max-w-prose text-sm text-muted-foreground sm:text-base">
        {{ description }}
      </p>
    </div>
    <Button v-if="link" variant="default-primary" @click="redirect">
      <BaseIcon name="HeroPlusIconSolid" class="h-5 w-5" />
      {{ t(`pages.${module}.emptyState.button`) }}
    </Button>
    <div v-if="secondaryButton || primaryButton" class="mb-5 flex flex-col gap-2 sm:flex-row">
      <Button
        v-if="primaryButton"
        :variant="primaryButton.isOutlined ? 'outline' : 'default-primary'"
        :disabled="primaryButton.isLoading"
        @click="emit('onClick', 'primaryButton')"
      >
        <Loader2 v-if="primaryButton.isLoading" class="animate-spin" />
        <BaseIcon v-else-if="primaryButton.icon" :name="primaryButton.icon" class="h-5 w-5" />
        {{ primaryButton.text }}
      </Button>
      <Button
        v-if="secondaryButton && !secondaryButton.link"
        :variant="secondaryButton.isOutlined ? 'outline' : 'default-primary'"
        :disabled="secondaryButton.isLoading"
        @click="emit('onClick', 'secondaryButton')"
      >
        <Loader2 v-if="secondaryButton.isLoading" class="animate-spin" />
        <BaseIcon v-else-if="secondaryButton.icon" :name="secondaryButton.icon" class="h-5 w-5" />
        {{ secondaryButton.text }}
      </Button>
      <a
        v-else-if="secondaryButton && secondaryButton.link"
        :href="secondaryButton.link"
        target="_blank"
        class="flex flex-row items-center font-medium text-primary gap-2"
      >
        <BaseIcon v-if="secondaryButton.icon" :name="secondaryButton.icon" class="h-5 w-5" />
        {{ secondaryButton.text }}
      </a>
    </div>
  </div>
</template>
